import { useCallback, useEffect, useState } from 'react';
import * as Tone from 'tone';

/**
 * Which mic to sing into and which speakers or headphones to play through
 * (desktop), remembered on this device. Every mic opened for singing (graded
 * takes, the scratch recorder, the pitch monitor) uses the chosen input;
 * Tone's audio (the song, the metronome, playing takes back) and the
 * scratch recorder's playback go to the chosen output.
 *
 * Choosing an output needs AudioContext.setSinkId, which Chrome and Edge
 * have and Firefox does not. Device ids last per site once the mic is
 * allowed; a device's name is kept too, so it is found again if its id
 * changes.
 */

const INPUT_KEY = 'musiker-audio-input';
const OUTPUT_KEY = 'musiker-audio-output';

const read = (key) => {
  try {
    const stored = JSON.parse(localStorage.getItem(key));
    return stored && typeof stored.id === 'string' ? stored : null;
  } catch (e) {
    return null;
  }
};
const write = (key, device) => {
  try {
    if (device) localStorage.setItem(key, JSON.stringify({ id: device.id, label: device.label }));
    else localStorage.removeItem(key);
  } catch (e) {
    // Not remembered, still used now
  }
};

const listeners = new Set();
/** Calls listener('input' | 'output') when a choice changes; returns an unsubscribe. */
export function onAudioDeviceChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
const announce = (kind) => listeners.forEach(listener => listener(kind));

const VOICE = { echoCancellation: false, noiseSuppression: false, autoGainControl: false };

/**
 * Opens the chosen mic for singing (voice-call processing off: it gates
 * sustained vowels and pumps the level), or the default one if the chosen
 * mic is gone.
 */
export async function openSingingMic() {
  const chosen = read(INPUT_KEY);
  let stream = null;
  if (chosen) {
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { ...VOICE, deviceId: { exact: chosen.id } } });
    } catch (error) {
      if (error.name !== 'OverconstrainedError' && error.name !== 'NotFoundError') throw error;
    }
  }
  if (!stream) stream = await navigator.mediaDevices.getUserMedia({ audio: VOICE });
  // The mic being allowed reveals device names to the device list
  announce('opened');
  return stream;
}

/** Whether this is Windows, whose mic "Audio enhancements" duck and chop singing. */
export const onWindows = () => /Windows/i.test(navigator.userAgentData?.platform || navigator.userAgent || '');

/** Whether this browser can send audio to a chosen output. */
export const canChooseOutput = () => typeof window.AudioContext === 'function'
  && typeof window.AudioContext.prototype.setSinkId === 'function';

/**
 * Sends a context's sound to the chosen output (the default if none is
 * chosen or it is gone). Takes a plain AudioContext or Tone's.
 */
export async function routeToOutput(context) {
  // Tone wraps the browser's context; the wrapper keeps it as _nativeContext
  const native = context?._nativeContext || context;
  if (!native || typeof native.setSinkId !== 'function' || native.state === 'closed') return;
  const chosen = read(OUTPUT_KEY);
  try {
    if ((native.sinkId || '') !== (chosen?.id || '')) await native.setSinkId(chosen?.id || '');
  } catch (e) {
    if (chosen) {
      try {
        await native.setSinkId('');
      } catch (ignored) {
        // Stays where it was
      }
    }
  }
}

const routeTone = () => routeToOutput(Tone.getContext().rawContext);

/**
 * The devices to choose from and the choices, for the Record group:
 * { inputs, outputs, input, output, setInput, setOutput, canChooseOutput,
 * named, windows }, where inputs/outputs are [{ id, label }] and
 * input/output the chosen ids ('' for the system default). `named` is
 * false until the mic has been allowed (the browser hides device names
 * until then).
 */
export function useAudioDevices(active) {
  const [devices, setDevices] = useState({ inputs: [], outputs: [], named: false });
  const [input, setInputState] = useState(() => read(INPUT_KEY)?.id || '');
  const [output, setOutputState] = useState(() => read(OUTPUT_KEY)?.id || '');

  const refresh = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    let list = [];
    try {
      list = await navigator.mediaDevices.enumerateDevices();
    } catch (e) {
      return;
    }
    // The system default shows up as its own entry ('default' / 'communications'); the empty choice stands for it
    const pick = kind => list
      .filter(d => d.kind === kind && d.deviceId && d.deviceId !== 'default' && d.deviceId !== 'communications')
      .map(d => ({ id: d.deviceId, label: d.label }));
    const inputs = pick('audioinput');
    const outputs = pick('audiooutput');
    // A remembered device whose id changed is found again by its name
    [[INPUT_KEY, inputs, setInputState, 'input'], [OUTPUT_KEY, outputs, setOutputState, 'output']].forEach(([key, found, set, kind]) => {
      const chosen = read(key);
      if (!chosen || found.some(d => d.id === chosen.id)) return;
      const same = chosen.label && found.find(d => d.label === chosen.label);
      if (same) {
        write(key, same);
        set(same.id);
        announce(kind);
      }
    });
    setDevices({ inputs, outputs, named: list.some(d => d.label) });
  }, []);

  useEffect(() => {
    if (!active) return undefined;
    refresh();
    routeTone();
    const media = navigator.mediaDevices;
    media?.addEventListener?.('devicechange', refresh);
    return () => media?.removeEventListener?.('devicechange', refresh);
  }, [active, refresh]);

  // The mic being allowed reveals device names: look again when a mic opens
  useEffect(() => onAudioDeviceChange((kind) => { if (kind === 'opened') refresh(); }), [refresh]);

  const setInput = useCallback((id) => {
    const device = devices.inputs.find(d => d.id === id) || null;
    write(INPUT_KEY, device);
    setInputState(device ? device.id : '');
    announce('input');
  }, [devices.inputs]);

  const setOutput = useCallback((id) => {
    const device = devices.outputs.find(d => d.id === id) || null;
    write(OUTPUT_KEY, device);
    setOutputState(device ? device.id : '');
    routeTone();
    announce('output');
  }, [devices.outputs]);

  return { ...devices, input, output, setInput, setOutput, canChooseOutput: canChooseOutput(), windows: onWindows() };
}
